import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowClockwise, Funnel, X } from "@phosphor-icons/react";
import api, { CATEGORIES, categoryLabel } from "../lib/api";
import CompetitionCard from "../components/CompetitionCard";
import BudgetModule from "../components/BudgetModule";
import SEO from "../components/SEO";
import { publicCompetitionOperators } from "../lib/publicOperators";
import {
  catalogueQueryKey,
  DEFAULT_CATALOGUE_QUERY_KEY,
  fetchCatalogueFirstPage,
  fetchCataloguePage,
  isCatalogueAbort,
  isDefaultCatalogueQuery,
  LEGACY_CATALOGUE_SNAPSHOT_ID,
  readCatalogueFirstPage,
  writeCatalogueFirstPage,
} from "../lib/publicCatalogue";

const SORTS = [
  { value: "overall", label: "Overall CompCheck" },
  { value: "odds", label: "Best odds" },
  { value: "value", label: "Best value" },
  { value: "closing_soon", label: "Closing soon" },
  { value: "price_low", label: "Price: low to high" },
  { value: "price_high", label: "Price: high to low" },
];

const finalCategoryForFilter = (comp) => {
  const category = String(comp?.category || "").trim().toLowerCase();
  if (category === "car" || category === "vehicle" || category === "vehicles") return "cars";
  if (category === "bike" || category === "motorbike" || category === "motorcycle") return "bikes";
  if (category === "watch") return "watches";
  return category;
};

const mergeSearchParam = (current, key, value) => {
  const next = new URLSearchParams(current);
  if (value) next.set(key, value);
  else next.delete(key);
  return next;
};

const uniqueCompetitionRows = (rows, existing = []) => {
  const ids = new Set(existing.map((row) => row?.id).filter(Boolean));
  const slugs = new Set(existing.map((row) => row?.slug).filter(Boolean));
  const unique = [...existing];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if ((row?.id && ids.has(row.id)) || (row?.slug && slugs.has(row.slug))) return;
    unique.push(row);
    if (row?.id) ids.add(row.id);
    if (row?.slug) slugs.add(row.slug);
  });
  return unique;
};

const emptyCatalogue = () => ({
  queryKey: null,
  snapshotId: null,
  publishedAt: null,
  total: 0,
  items: [],
  nextCursor: null,
  hasMore: false,
  updateAvailable: false,
});

const catalogueFromPage = (page, queryKey) => ({
  queryKey,
  snapshotId: page.snapshot_id,
  publishedAt: page.published_at,
  total: page.matching_total ?? page.total,
  items: page.items,
  nextCursor: page.next_cursor,
  hasMore: page.has_more,
  updateAvailable: page.update_available,
});

const verifiedTime = (value) => {
  if (!value) return "an earlier verified publication";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "an earlier verified publication";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export default function CompetitionsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const latestSearchParams = useRef(new URLSearchParams(searchParamsKey));
  const filters = useMemo(
    () => ({
      q: searchParams.get("q") || "",
      category: searchParams.get("category") || "",
      operator_id: searchParams.get("operator_id") || "",
      min_price: searchParams.get("min_price") || "",
      max_price: searchParams.get("max_price") || "",
      sort: searchParams.get("sort") || "overall",
    }),
    [searchParams]
  );
  const queryKey = useMemo(() => catalogueQueryKey(filters), [filters]);
  // Persist only the canonical unfiltered first page. Search text and other
  // user-entered filter state never enter long-lived browser storage.
  const persistFirstPage = useMemo(() => isDefaultCatalogueQuery(filters), [filters]);
  const [catalogue, setCatalogue] = useState(() => {
    const cached = readCatalogueFirstPage(
      persistFirstPage ? queryKey : DEFAULT_CATALOGUE_QUERY_KEY,
    );
    return cached
      ? catalogueFromPage(
        cached.page,
        persistFirstPage ? queryKey : DEFAULT_CATALOGUE_QUERY_KEY,
      )
      : emptyCatalogue();
  });
  const catalogueRef = useRef(catalogue);
  const [operators, setOperators] = useState([]);
  const [operatorLoadState, setOperatorLoadState] = useState("loading");
  const [phase, setPhase] = useState(catalogue.snapshotId ? "checking" : "loading");
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [open, setOpen] = useState(false);
  const catalogueRequest = useRef({ generation: 0, controller: null });
  const operatorRequest = useRef({ generation: 0, controller: null });
  const loadingMoreRef = useRef(false);
  const comps = catalogue.items;
  const total = catalogue.total;
  const hasMore = catalogue.hasMore;
  const publicOperators = useMemo(
    () => publicCompetitionOperators(operators),
    [operators]
  );
  const displayComps = useMemo(
    () => {
      if (catalogue.queryKey !== queryKey) return [];
      if (!filters.category) return comps;
      return comps.filter((comp) => finalCategoryForFilter(comp) === filters.category);
    },
    [catalogue.queryKey, comps, filters.category, queryKey]
  );

  const loadOperators = useCallback(() => {
    const generation = operatorRequest.current.generation + 1;
    operatorRequest.current.controller?.abort();
    const controller = new AbortController();
    operatorRequest.current = { generation, controller };
    setOperatorLoadState("loading");
    api.get("/operators", {
      signal: controller.signal,
      timeout: 2500,
      publicOnly: true,
      withCredentials: false,
    }).then((response) => {
      if (controller.signal.aborted || operatorRequest.current.generation !== generation) return;
      if (!Array.isArray(response.data)) throw new Error("Invalid public operator response");
      setOperators(response.data);
      setOperatorLoadState("ready");
    }).catch(() => {
      if (controller.signal.aborted || operatorRequest.current.generation !== generation) return;
      setOperatorLoadState("error");
    }).finally(() => {
      if (operatorRequest.current.generation === generation) {
        operatorRequest.current.controller = null;
      }
    });
  }, []);

  useEffect(() => {
    loadOperators();
    return () => operatorRequest.current.controller?.abort();
  }, [loadOperators]);

  useEffect(() => {
    latestSearchParams.current = new URLSearchParams(searchParamsKey);
  }, [searchParamsKey]);

  const commitCatalogue = useCallback((next) => {
    catalogueRef.current = next;
    setCatalogue(next);
  }, []);

  const refreshCatalogue = useCallback(() => {
    const generation = catalogueRequest.current.generation + 1;
    catalogueRequest.current.controller?.abort();
    const controller = new AbortController();
    catalogueRequest.current = { generation, controller };
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setLoadMoreError(null);
    const cached = persistFirstPage ? readCatalogueFirstPage(queryKey) : null;
    const fallback = cached || readCatalogueFirstPage(DEFAULT_CATALOGUE_QUERY_KEY);
    // Keep whichever verified result the visitor is already looking at while a
    // filter or refresh request is outstanding. The persisted canonical page is
    // only an initial fallback; it must not replace a newer in-memory result.
    if (!catalogueRef.current.snapshotId && fallback) {
      commitCatalogue(catalogueFromPage(
        fallback.page,
        cached ? queryKey : DEFAULT_CATALOGUE_QUERY_KEY,
      ));
    }
    const hasFallback = Boolean(fallback?.page || catalogueRef.current.snapshotId);
    setPhase(hasFallback ? "checking" : "loading");

    fetchCatalogueFirstPage({ filters, signal: controller.signal, cached })
      .then((result) => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        const next = catalogueFromPage(result.page, queryKey);
        commitCatalogue(next);
        if (result.cacheable && persistFirstPage) {
          writeCatalogueFirstPage(queryKey, result);
        }
        setPhase("ready");
        result.summaryRefresh.then((summaryResult) => {
          if (
            !result.pageCacheable
            || !summaryResult?.cacheable
            || summaryResult.summary.snapshot_id !== result.page.snapshot_id
            || controller.signal.aborted
            || catalogueRequest.current.generation !== generation
            || !persistFirstPage
          ) return;
          writeCatalogueFirstPage(queryKey, {
            ...result,
            summary: summaryResult.summary,
            summaryEtag: summaryResult.etag,
          });
        });
      })
      .catch((requestError) => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        if (isCatalogueAbort(requestError)) return;
        setPhase(catalogueRef.current.snapshotId ? "stale" : "error");
      })
      .finally(() => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        catalogueRequest.current.controller = null;
      });

    return controller;
  }, [commitCatalogue, filters, persistFirstPage, queryKey]);

  useEffect(() => {
    const controller = refreshCatalogue();
    return () => controller.abort();
  }, [refreshCatalogue]);

  useEffect(() => {
    const refreshWhenOnline = () => refreshCatalogue();
    window.addEventListener("online", refreshWhenOnline);
    return () => window.removeEventListener("online", refreshWhenOnline);
  }, [refreshCatalogue]);

  const loadMore = () => {
    const current = catalogueRef.current;
    if (
      phase === "loading"
      || phase === "checking"
      || loadingMoreRef.current
      || !current.hasMore
      || !current.nextCursor
      || current.queryKey !== queryKey
    ) return;
    const generation = catalogueRequest.current.generation;
    const controller = new AbortController();
    catalogueRequest.current.controller?.abort();
    catalogueRequest.current.controller = controller;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);
    fetchCataloguePage({
      filters,
      cursor: current.nextCursor,
      signal: controller.signal,
    })
      .then(({ page }) => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        const latest = catalogueRef.current;
        if (
          latest.queryKey !== queryKey
          || latest.snapshotId !== current.snapshotId
          || page.snapshot_id !== current.snapshotId
        ) {
          throw new Error("catalogue_snapshot_changed");
        }
        const next = {
          ...latest,
          total: page.matching_total ?? page.total,
          items: uniqueCompetitionRows(page.items, latest.items),
          nextCursor: page.next_cursor,
          hasMore: page.has_more,
          updateAvailable: page.update_available,
        };
        commitCatalogue(next);
      })
      .catch((requestError) => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        if (!isCatalogueAbort(requestError)) setLoadMoreError(requestError);
      })
      .finally(() => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        catalogueRequest.current.controller = null;
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  };

  const update = (k, v) => {
    const next = mergeSearchParam(latestSearchParams.current, k, v);
    latestSearchParams.current = next;
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => {
    const next = new URLSearchParams();
    latestSearchParams.current = next;
    setSearchParams(next, { replace: true });
  };

  const showingRequestedQuery = catalogue.queryKey === queryKey;
  const hasRequestedCatalogue = Boolean(catalogue.snapshotId && showingRequestedQuery);
  const loadingWithoutCatalogue = !hasRequestedCatalogue && (phase === "loading" || phase === "checking");
  const failedWithoutCatalogue = !hasRequestedCatalogue && (phase === "error" || phase === "stale");
  const statusText = (() => {
    if (loadingWithoutCatalogue) return "Loading competitions for these filters…";
    if (failedWithoutCatalogue) return "We couldn’t load the latest competitions. Please try again.";
    if (catalogue.snapshotId === LEGACY_CATALOGUE_SNAPSHOT_ID) {
      return "Verified snapshot migration is pending; showing the current live catalogue.";
    }
    if (phase === "stale") {
      return `Showing earlier results for these filters from ${verifiedTime(catalogue.publishedAt)}. We couldn’t check for updates.`;
    }
    if (phase === "checking") {
      return showingRequestedQuery
        ? `Checking for the latest competitions. You can keep browsing ${total} results.`
        : "Loading competitions for these filters…";
    }
    if (catalogue.updateAvailable) {
      return `Showing ${total} verified competitions. A newer verified catalogue is available.`;
    }
    return `${total} competition${total === 1 ? "" : "s"} matching your filters`;
  })();

  const FiltersPanel = () => (
    <div className="space-y-6">
      <div>
        <label className="overline text-white/50 block mb-2">Search</label>
        <input
          value={filters.q}
          onChange={(e) => update("q", e.target.value)}
          placeholder="Porsche, Rolex, cash..."
          data-testid="filter-search-input"
          className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm focus:border-[var(--cc-gold)] outline-none"
        />
      </div>
      <div>
        <label className="overline text-white/50 block mb-2">Category</label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => update("category", "")}
            data-testid="filter-category-all"
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              !filters.category ? "bg-[var(--cc-gold)] border-[var(--cc-gold)] text-[var(--cc-navy)]" : "border-white/20 text-white/70 hover:border-white/40"
            }`}
          >
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => update("category", c)}
              data-testid={`filter-category-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                filters.category === c ? "bg-[var(--cc-gold)] border-[var(--cc-gold)] text-[var(--cc-navy)]" : "border-white/20 text-white/70 hover:border-white/40"
              }`}
            >
              {categoryLabel(c)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="overline text-white/50 block mb-2">Operator</label>
        <select
          value={filters.operator_id}
          onChange={(e) => update("operator_id", e.target.value)}
          data-testid="filter-operator"
          aria-busy={operatorLoadState === "loading"}
          className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-[var(--cc-gold)] outline-none"
        >
          <option value="" className="bg-[var(--cc-navy-2)] text-white">All operators</option>
          {publicOperators.map((o) => (
            <option key={o.id} value={o.id} className="bg-[var(--cc-navy-2)] text-white">{o.name}</option>
          ))}
        </select>
        <div className="min-h-[24px] flex items-center gap-2 text-xs text-white/60" role="status">
          {operatorLoadState === "loading" && "Loading operators..."}
          {operatorLoadState === "error" && (
            <>
              <span>Operator filters unavailable</span>
              <button
                type="button"
                onClick={loadOperators}
                aria-label="Retry operator filters"
                title="Retry operator filters"
                className="p-1 text-white/80 hover:text-white focus-visible:outline focus-visible:outline-2"
              >
                <ArrowClockwise size={16} aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="overline text-white/50 block mb-2">Min £</label>
          <input
            type="number"
            min="0"
            value={filters.min_price}
            onChange={(e) => update("min_price", e.target.value)}
            data-testid="filter-min-price"
            className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm focus:border-[var(--cc-gold)] outline-none"
          />
        </div>
        <div>
          <label className="overline text-white/50 block mb-2">Max £</label>
          <input
            type="number"
            min="0"
            value={filters.max_price}
            onChange={(e) => update("max_price", e.target.value)}
            data-testid="filter-max-price"
            className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm focus:border-[var(--cc-gold)] outline-none"
          />
        </div>
      </div>
      <button
        onClick={clearAll}
        data-testid="filter-clear-all"
        className="text-xs text-white/60 underline hover:text-white"
      >
        Clear all filters
      </button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <SEO
        title={filters.category ? `${categoryLabel(filters.category)} competition odds and value` : "UK Competitions | Compare Live Prize Competitions & Odds"}
        exactTitle={!filters.category}
        description={filters.category
          ? `Browse ${categoryLabel(filters.category).toLowerCase()} competitions in the UK. Compare ticket price, estimated odds, prize value, free-entry clarity and the CompCheck Score before you enter.`
          : "Browse live UK competitions and compare ticket prices, ticket caps, calculated odds, closing dates and operators across cars, cash, bikes, tech and more."}
        path="/competitions"
        ogType="website"
        queryString={searchParamsKey}
      />
      <div className="mb-8">
        <div className="overline text-[var(--cc-gold)] mb-2">Browse</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">Compare UK Competitions</h1>
        <p className="text-[var(--cc-text-muted)] mt-3 leading-relaxed max-w-3xl">
          Browse live prize competitions from tracked UK operators. Compare the
          current ticket price, trusted competition-wide ticket cap, calculated
          maximum-pool odds, closing date and source details before choosing whether
          to visit an operator. Where a reliable total cap is unavailable, the card
          states that odds are unavailable instead of substituting a per-user limit.
        </p>
        <p
          className="text-[var(--cc-text-muted)] mt-3 text-sm"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          id="catalogue-status"
          data-testid="catalogue-status"
        >
          {statusText}
        </p>
        {(phase === "stale" || catalogue.updateAvailable) && (
          <button
            type="button"
            onClick={refreshCatalogue}
            className="mt-3 min-h-11 rounded border border-white/20 px-4 py-2 text-sm text-[var(--cc-gold)] hover:border-[var(--cc-gold)]"
            data-testid="catalogue-refresh-btn"
          >
            Check again
          </button>
        )}
        <nav className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm" aria-label="Competition comparison help">
          <Link className="text-[var(--cc-gold)] hover:underline" to="/how-we-score">How scoring works</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/guides/how-competition-odds-are-calculated">How odds are calculated</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/uk-competitions">UK competition guide</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/uk-car-competitions">Compare car competitions</Link>
          <Link className="text-[var(--cc-gold)] hover:underline" to="/data-accuracy">Data accuracy</Link>
        </nav>
      </div>

      <BudgetModule surface="catalogue" />

      <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-10">
        {/* Mobile filter toggle */}
        <div className="flex items-center justify-between mb-4 lg:hidden gap-3">
          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-filters-open"
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-white/15 text-sm"
          >
            <Funnel size={16} /> Filters
          </button>
          <select
            value={filters.sort}
            onChange={(e) => update("sort", e.target.value)}
            data-testid="sort-select-mobile"
            className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm text-white"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value} className="bg-[var(--cc-navy-2)] text-white">{s.label}</option>)}
          </select>
        </div>

        {/* Desktop sidebar */}
        <aside className="hidden lg:block sticky top-24 h-fit border border-white/10 rounded-lg p-6 bg-white/[0.02]">
          <FiltersPanel />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 bg-[var(--cc-navy)]/95 backdrop-blur p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl">Filters</h3>
              <button onClick={() => setOpen(false)} data-testid="mobile-filters-close"><X size={24} /></button>
            </div>
            <FiltersPanel />
            <button
              onClick={() => setOpen(false)}
              className="btn-gold w-full mt-8 py-3 rounded text-sm"
              data-testid="mobile-filters-apply"
            >
              Show {total} competitions
            </button>
          </div>
        )}

        <div
          aria-busy={phase === "loading" || phase === "checking"}
          aria-describedby="catalogue-status"
        >
          <div className="hidden lg:flex items-center justify-end mb-5">
            <select
              value={filters.sort}
              onChange={(e) => update("sort", e.target.value)}
              data-testid="sort-select"
              className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm text-white"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value} className="bg-[var(--cc-navy-2)] text-white">{s.label}</option>)}
            </select>
          </div>
          {loadingWithoutCatalogue ? (
            <div
              className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
              data-testid="catalogue-skeletons"
              aria-hidden="true"
            >
              {Array.from({ length: 6 }, (_, index) => (
                <div
                  key={index}
                  className="h-96 animate-pulse rounded-lg border border-white/10 bg-white/[0.04]"
                />
              ))}
            </div>
          ) : failedWithoutCatalogue ? (
            <div
              className="rounded-lg border border-amber-300/30 bg-amber-300/5 p-8 text-center"
              role="alert"
              data-testid="catalogue-error"
            >
              <p className="font-semibold">We couldn’t load the latest competitions. Please try again.</p>
              <p className="mt-2 text-sm text-[var(--cc-text-muted)]">
                Check your connection, then try again. This page will also retry when you reconnect.
              </p>
              <button
                type="button"
                onClick={refreshCatalogue}
                className="mt-5 min-h-11 rounded border border-[var(--cc-gold)] px-5 py-2 text-sm text-[var(--cc-gold)]"
                data-testid="catalogue-retry-btn"
              >
                Retry catalogue
              </button>
            </div>
          ) : displayComps.length === 0 && showingRequestedQuery ? (
            <div className="border border-dashed border-white/15 rounded-lg p-12 text-center">
              <p className="text-[var(--cc-text-muted)]">No competitions match these filters. Try changing your selection.</p>
              <button onClick={clearAll} className="mt-4 text-sm text-[var(--cc-gold)] underline">Clear filters</button>
            </div>
          ) : (
            <>
              <div
                className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
                data-catalogue-snapshot={catalogue.snapshotId}
              >
                {displayComps.map((c, i) => (
                  <CompetitionCard
                    key={c.id}
                    comp={c}
                    index={i}
                    snapshotId={catalogue.snapshotId}
                  />
                ))}
              </div>
              {hasMore && showingRequestedQuery && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="load-more-btn"
                    className="px-6 py-3 rounded-full border border-white/20 text-sm text-white/80 hover:border-[var(--cc-gold)] hover:text-[var(--cc-gold)] transition-colors"
                  >
                    {loadingMore
                      ? "Loading more..."
                      : `Load more (${Math.max(total - displayComps.length, 0)} remaining)`}
                  </button>
                </div>
              )}
              {loadMoreError && (
                <div className="mt-5 text-center" role="alert" data-testid="load-more-error">
                  <p className="text-sm text-amber-200">
                    More competitions could not be loaded. Your current results are unchanged.
                  </p>
                  <button
                    type="button"
                    onClick={loadMore}
                    className="mt-3 min-h-11 rounded border border-white/20 px-5 py-2 text-sm"
                    data-testid="load-more-retry-btn"
                  >
                    Retry more results
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <section className="mt-16 border-t border-white/10 pt-10" data-testid="catalogue-methodology-copy">
        <div className="grid gap-8 md:grid-cols-3">
          <div>
            <h2 className="font-display text-xl font-semibold">What the listings represent</h2>
            <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
              Each public card belongs to an approved operator identity and has
              passed the relevant publication and visibility contract. Operators
              remain in the directory even when they temporarily have no public records.
            </p>
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold">How to use filters</h2>
            <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
              Filters help narrow the current catalogue; they do not create separate
              editorial rankings or endorsements. Search and filter URLs are kept
              out of the index so the stable catalogue remains the canonical page.
            </p>
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold">Check the live source</h2>
            <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
              Prices, ticket availability, dates and terms can change. Open the
              official operator source and review its terms and free-entry route
              before entering.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
